<script setup lang="ts">
import SectionRepository from '@legacy/js/repository/SectionRepository';
import EventBus from '@legacy/js/service/EventBus';
import axios from 'axios';
import { Pencil, Trash2 } from 'lucide';
import { onMounted, ref } from 'vue';
import { SphButton } from '@/components/base/button';
import { SphInput } from '@/components/base/input';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

defineOptions({
    compatConfig: { MODE: 3 },
});

const props = defineProps<{
    federationId: number
}>();

const { i18n } = useI18n();
const { notify } = useNotifications();

interface Section {
    id: number
    name: string
}

const sections = ref<Section[]>([]);
const newName = ref('');
const editNames = ref<Record<number, string>>({});
const pendingCreate = ref(false);
const pendingUpdate = ref(false);

function load() {
    axios.get(`/federations/${props.federationId}/sections`)
        .then((r) => {
            sections.value = r.data.data.map((d: any) => ({ id: d.id, name: d.name }));
        })
        .catch(() => notify.error('error.loadingData'));
}

onMounted(load);

function startEdit(section: Section) {
    if (!(section.id in editNames.value)) {
        editNames.value[section.id] = section.name;
    }
}

const isEditing = (section: Section) => section.id in editNames.value;

function create() {
    if (!newName.value) {
        return;
    }
    pendingCreate.value = true;
    SectionRepository.create(props.federationId, newName.value)
        .then(() => {
            newName.value = '';
            notify.success('saveChanges.success');
            load();
            EventBus.$emit('section:changed', props.federationId);
        })
        .catch((error: any) => notify.error(error.message || 'saveChanges.error'))
        .finally(() => {
            pendingCreate.value = false;
        });
}

function update() {
    const toUpdate = sections.value
        .filter((s) => s.id in editNames.value)
        .map((s) => ({ id: s.id, name: editNames.value[s.id] }));
    if (!toUpdate.length) {
        return;
    }
    pendingUpdate.value = true;
    SectionRepository.update(toUpdate)
        .then(() => {
            editNames.value = {};
            notify.success('saveChanges.success');
            load();
            EventBus.$emit('section:changed', props.federationId);
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => {
            pendingUpdate.value = false;
        });
}

function save() {
    if (Object.keys(editNames.value).length > 0) {
        update();
    }
    if (newName.value) {
        create();
    }
}

function remove(section: Section) {
    ;(window as any).showConfirm(i18n.t('confirm'), i18n.t('section.confirm.delete', { name: section.name }), () => {
        SectionRepository.delete(section.id)
            .then(() => {
                sections.value = sections.value.filter((s) => s.id !== section.id);
                delete editNames.value[section.id];
                notify.success('section.delete.success', { name: section.name });
                EventBus.$emit('section:changed', props.federationId);
            })
            .catch((error: any) => {
                if (error.status === 409) {
                    notify.error('section.delete.conflict', { name: section.name });
                } else {
                    notify.error('saveChanges.error');
                }
            });
    }, 'Ok');
}

const isSavable = () => Object.keys(editNames.value).length > 0 || newName.value !== '';
</script>

<template>
    <table class="kgr-table kgr-table--striped kgr-table--block table-groups kgr-row text-foreground">
        <thead>
            <tr class="kgr-table__row">
                <th class="kgr-table__cell kgr-table__cell--headline w-full">
                    {{ i18n.t('name') }}
                </th>
                <th class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--actions"></th>
            </tr>
        </thead>
        <tbody>
            <tr class="kgr-table__row">
                <td class="kgr-table__cell">
                    <SphInput
                        v-model="newName"
                        :maxlength="100"
                    />
                </td>
                <td class="kgr-table__cell"></td>
            </tr>
            <tr
                v-for="section in sections"
                :key="section.id"
                class="kgr-table__row"
            >
                <td class="kgr-table__cell">
                    <SphInput
                        v-if="isEditing(section)"
                        v-model="editNames[section.id]"
                        :maxlength="100"
                    />
                    <template v-else>
                        {{ section.name }}
                    </template>
                </td>
                <td class="kgr-table__cell kgr-table__cell--actions">
                    <SphButton
                        :icon="Pencil"
                        variant="transparent"
                        size="md"
                        :disabled="isEditing(section)"
                        class="kgr-table__action"
                        @click="startEdit(section)"
                    />
                    <SphButton
                        :icon="Trash2"
                        variant="transparent"
                        size="md"
                        class="kgr-table__action"
                        @click="remove(section)"
                    />
                </td>
            </tr>
        </tbody>
    </table>
    <div class="kgr-text kgr-text--rightAligned">
        <SphButton
            :label="i18n.t('saveChanges')"
            :disabled="!isSavable()"
            :loading="pendingCreate || pendingUpdate"
            @click="save"
        />
    </div>
</template>
